const naturalTextCollator = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: "base",
});

const isMissingSortValue = (value) =>
  value === null ||
  value === undefined ||
  value === "" ||
  (typeof value === "number" && Number.isNaN(value)) ||
  (value instanceof Date && Number.isNaN(value.getTime()));

const compareSortValues = (a, b, direction) => {
  const aIsMissing = isMissingSortValue(a);
  const bIsMissing = isMissingSortValue(b);

  if (aIsMissing || bIsMissing) {
    if (aIsMissing && bIsMissing) return 0;
    return aIsMissing ? 1 : -1;
  }

  let comparison;
  if (typeof a === "number" && typeof b === "number") {
    comparison = a - b;
  } else {
    comparison = naturalTextCollator.compare(String(a), String(b));
  }

  return direction === "asc" ? comparison : -comparison;
};

const parseAppointmentTime = (time) => {
  if (typeof time !== "string") return null;

  const match = time.trim().match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?$/i);
  if (!match) return null;

  let hours = Number(match[1]);
  const minutes = Number(match[2]);
  const seconds = Number(match[3] || 0);
  const period = match[4]?.toUpperCase();

  if (minutes > 59 || seconds > 59) return null;

  if (period) {
    if (hours < 1 || hours > 12) return null;
    if (period === "AM" && hours === 12) hours = 0;
    if (period === "PM" && hours !== 12) hours += 12;
  } else if (hours > 23) {
    return null;
  }

  return ((hours * 60 + minutes) * 60 + seconds) * 1000;
};

const parseAppointmentDate = (date) => {
  if (!date) return null;

  if (date instanceof Date) {
    return Number.isNaN(date.getTime()) ? null : date;
  }

  if (typeof date?.toDate === "function") {
    const parsedDate = date.toDate();
    return parsedDate instanceof Date && !Number.isNaN(parsedDate.getTime())
      ? parsedDate
      : null;
  }

  if (typeof date !== "string") return null;

  const dateOnlyMatch = date.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch;
    const parsedDate = new Date(Number(year), Number(month) - 1, Number(day));
    if (
      parsedDate.getFullYear() !== Number(year) ||
      parsedDate.getMonth() !== Number(month) - 1 ||
      parsedDate.getDate() !== Number(day)
    ) {
      return null;
    }
    return parsedDate;
  }

  const parsedDate = new Date(date);
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

export const getAppointmentDateTimeValue = (appointment) => {
  const date = parseAppointmentDate(appointment?.date);
  const time = parseAppointmentTime(appointment?.time);
  if (!date || time === null) return null;

  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  ).getTime() + time;
};

export const getNextAppointmentSortConfig = (currentConfig, key) => {
  if (currentConfig?.key !== key) {
    return { key, direction: "asc" };
  }
  if (currentConfig.direction === "asc") {
    return { key, direction: "desc" };
  }
  return null;
};

export const sortAppointments = (appointments, sortConfig, getSortValue) => {
  const copiedAppointments = [...appointments];
  if (!sortConfig) return copiedAppointments;

  return copiedAppointments
    .map((appointment, index) => ({
      appointment,
      index,
      value: getSortValue(appointment, sortConfig.key),
    }))
    .sort((a, b) => {
      const comparison = compareSortValues(a.value, b.value, sortConfig.direction);
      return comparison || a.index - b.index;
    })
    .map(({ appointment }) => appointment);
};

export const getAppointmentSortIndicator = (sortConfig, key) => {
  if (sortConfig?.key !== key) return "⇅";
  return sortConfig.direction === "asc" ? "↑" : "↓";
};

export const getAppointmentSortTitle = (label, key, sortConfig) => {
  if (sortConfig?.key !== key) return `Sort by ${label}`;

  if (key === "dateTime") {
    return sortConfig.direction === "asc"
      ? "Sorted Earliest to Latest"
      : "Sorted Latest to Earliest";
  }
  if (key === "upcomingStatus") {
    return sortConfig.direction === "asc"
      ? "Sorted Confirmed to Accepted"
      : "Sorted Accepted to Confirmed";
  }
  if (key === "reference") {
    return sortConfig.direction === "asc"
      ? "Sorted by Reference (ascending)"
      : "Sorted by Reference (descending)";
  }

  return sortConfig.direction === "asc" ? "Sorted A–Z" : "Sorted Z–A";
};

export const getAppointmentTextValue = (value) =>
  typeof value === "string" ? value.replace(/<[^>]*>/g, "").trim() : value;
